import { useState } from 'react'
import {
  Bars3Icon,
  PencilIcon,
  ChevronDownIcon,
  CloudIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  XMarkIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline'
import type { PanelState } from '../../hooks/usePanelState'
import type { Song } from '../../lib/api'
import { UserDropdownMenu } from './UserDropdownMenu'

interface AppHeaderProps {
  panelState: PanelState
  currentSong?: Song | null
  saveStatus?: 'saved' | 'saving' | 'error' | 'offline' | undefined
  // Editor mode props
  isEditorMode?: boolean
  onBack?: () => void
  onSongUpdate?: (updates: Partial<Song>) => void
  hasUnsavedChanges?: boolean
  onSave?: () => void
  isSaving?: boolean
  autoSaveStatus?: 'saved' | 'saving' | 'pending' | 'error'
  // Settings modal props
  onOpenSettings?: () => void
  isSettingsOpen?: boolean
  settingsButtonRef?: React.RefObject<HTMLButtonElement | null>
}

const SAVE_STATUS_CONFIG = {
  saved: {
    icon: CheckCircleIcon,
    text: 'All changes saved',
    className: 'text-success-600'
  },
  saving: {
    icon: CloudIcon,
    text: 'Saving...',
    className: 'text-primary-600 animate-pulse'
  },
  error: {
    icon: ExclamationTriangleIcon,
    text: 'Error saving',
    className: 'text-red-600'
  },
  offline: {
    icon: ExclamationTriangleIcon,
    text: 'Offline - changes saved locally',
    className: 'text-warm-600'
  }
}

export function AppHeader({
  panelState,
  currentSong,
  saveStatus = undefined,
  isEditorMode = false,
  onBack,
  onSongUpdate,
  hasUnsavedChanges = false,
  onSave,
  isSaving = false,
  autoSaveStatus = 'saved',
  onOpenSettings,
  isSettingsOpen = false,
  settingsButtonRef
}: AppHeaderProps) {

  // Debug logging for header state (only when changes occur)
  if (typeof window !== 'undefined' && window.location.search.includes('debug')) {
    console.log('🎯 AppHeader render:', {
      isEditorMode,
      hasUnsavedChanges,
      isSaving,
      autoSaveStatus,
      saveStatus
    })
  }
  const [showSongMeta, setShowSongMeta] = useState(false)
  const [isEditingMeta, setIsEditingMeta] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editArtist, setEditArtist] = useState('')
  const [editStatus, setEditStatus] = useState<Song['status']>('draft')
  const [editTags, setEditTags] = useState('')

  const handleEditMetadata = () => {
    if (currentSong) {
      setEditTitle(currentSong.title)
      setEditArtist(currentSong.artist || '')
      setEditStatus(currentSong.status)
      setEditTags(currentSong.tags.join(', '))
      setIsEditingMeta(true)
      setShowSongMeta(true)
    }
  }

  const handleSaveMetadata = () => {
    if (onSongUpdate) {
      onSongUpdate({
        title: editTitle,
        artist: editArtist || undefined,
        status: editStatus,
        tags: editTags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
      })
    }
    setIsEditingMeta(false)
    setShowSongMeta(false)
  }

  const handleCancelEdit = () => {
    setIsEditingMeta(false)
    setShowSongMeta(false)
  }

  const saveConfig = saveStatus ? SAVE_STATUS_CONFIG[saveStatus] : null
  const SaveIcon = saveConfig?.icon

  return (
    <>
    <header className={
      isEditorMode
        ? "h-14 px-6 border-b shadow-sm transition-all duration-200"
        : "h-16 bg-gradient-to-r from-slate-900/80 via-slate-800/80 to-slate-900/80 border-b border-blue-400/20 shadow-xl transition-all duration-200"
    } style={
      isEditorMode
        ? {
            background: 'var(--editor-bg-primary)',
            borderColor: 'var(--editor-border-primary)'
          }
        : undefined
    }>
      <div className={isEditorMode ? "h-full flex items-center justify-between" : "h-full px-4 flex items-center justify-between"}>
        {/* Left Section - Back Button, Logo & Navigation */}
        <div className="flex items-center gap-4">
          {/* Back Button (Editor Mode Only) */}
          {isEditorMode && onBack && (
            <button
              data-testid="back-button"
              onClick={onBack}
              className="group flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10 transition-all duration-200 text-neutral-300 hover:text-white border border-white/20 hover:border-white/40 shadow-lg hover:shadow-xl backdrop-blur-sm transform hover:scale-105"
              title="Back to Song List"
            >
              <ArrowLeftIcon className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-300 ease-out" />
              <span className="font-medium hidden sm:inline">Back</span>
            </button>
          )}

          {/* Mobile Menu Button */}
          {panelState.isMobile && (
            <button
              onClick={() => setShowSongMeta(!showSongMeta)}
              className="p-2 rounded-md hover:bg-white/10 transition-all duration-200 lg:hidden backdrop-blur-sm border border-white/10 hover:border-white/20"
              aria-label="Toggle menu"
            >
              <Bars3Icon className="w-5 h-5 text-neutral-300 hover:text-white transition-colors duration-200" />
            </button>
          )}

          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-creative-500 flex items-center justify-center">
              <PencilIcon className="w-4 h-4 text-white" />
            </div>
            <div className="hidden sm:block">
              <img
                src="/assets/versifai_logo.svg"
                alt="Versifai"
                className="h-7 w-auto"
              />
              <p className="text-xs text-neutral-400">AI-Assisted Writing</p>
            </div>
          </div>

          {/* Song Metadata (Enhanced for Editor Mode) */}
          {currentSong && isEditorMode && (
            <div className="hidden md:flex items-center gap-2 ml-4 pl-4 border-l border-white/20">
              <button
                data-testid="song-details-toggle"
                onClick={() => setShowSongMeta(!showSongMeta)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-white/10 transition-all duration-200 group backdrop-blur-sm border border-white/20 hover:border-white/40 shadow-lg transform hover:scale-105"
              >
                <div className="text-left">
                  <div className="text-sm font-medium text-neutral-900 max-w-48 truncate">
                    {currentSong.title || 'Untitled Song'}
                  </div>
                  <div className="text-xs text-neutral-300 max-w-48 truncate flex items-center gap-2">
                    <span>{currentSong.artist || 'No artist'}</span>
                    <span>•</span>
                    <span className="capitalize">{(currentSong.status || 'draft').replace('_', ' ')}</span>
                  </div>
                </div>
                <ChevronDownIcon className={`w-4 h-4 text-neutral-400 transition-transform duration-300 ease-out ${showSongMeta ? 'rotate-180' : ''}`} />
              </button>
            </div>
          )}

          {/* Song Metadata (Simple for Non-Editor Mode) */}
          {currentSong && !isEditorMode && (
            <div className="hidden md:flex items-center gap-2 ml-4 pl-4 border-l border-white/20">
              <button
                onClick={() => setShowSongMeta(!showSongMeta)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-white/10 transition-all duration-200 group backdrop-blur-sm border border-white/10 hover:border-white/30 transform hover:scale-105"
              >
                <div className="text-left">
                  <div className="text-sm font-medium text-white max-w-48 truncate">
                    {currentSong.title || 'Untitled Song'}
                  </div>
                  <div className="text-xs text-neutral-300 max-w-48 truncate">
                    {currentSong.artist || 'No artist'} • {new Date(currentSong.updated_at).toLocaleDateString()}
                  </div>
                </div>
                <ChevronDownIcon className={`w-4 h-4 text-neutral-400 transition-transform duration-300 ease-out ${showSongMeta ? 'rotate-180' : ''}`} />
              </button>
            </div>
          )}
        </div>

        {/* Center Section - Simplified */}
        <div className="flex-1"></div>

        {/* Right Section - View Controls & Status */}
        <div className="flex items-center gap-4">
          {/* Google Docs Style Save Button (Editor Mode Only) */}
          {isEditorMode && onSave && (
            <button
              data-testid="save-button"
              onClick={onSave}
              disabled={!hasUnsavedChanges || isSaving || autoSaveStatus === 'saving'}
              className={`relative overflow-hidden px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 min-w-[120px] backdrop-blur-sm transform ${
                hasUnsavedChanges && !isSaving && autoSaveStatus !== 'saving'
                  ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 hover:from-blue-500/30 hover:to-purple-500/30 text-white border border-blue-400/40 hover:border-blue-400/60 shadow-lg hover:shadow-xl hover:scale-105'
                  : isSaving || autoSaveStatus === 'saving'
                  ? 'bg-black/20 text-neutral-300 border border-white/20 shadow-lg cursor-wait'
                  : 'bg-black/10 text-neutral-500 border border-white/10 shadow-lg cursor-default'
              }`}
              title={
                isSaving
                  ? 'Saving changes...'
                  : autoSaveStatus === 'saving'
                  ? 'Auto-saving changes...'
                  : hasUnsavedChanges && autoSaveStatus === 'pending'
                  ? 'Click to save now or wait for auto-save'
                  : hasUnsavedChanges
                  ? 'Click to save changes now'
                  : 'All changes saved'
              }
            >
              <span className="relative flex items-center justify-center gap-2" data-testid="auto-save-indicator">
                {isSaving ? (
                  <>
                    <div className="w-3 h-3 border-2 border-neutral-300 border-t-primary-500 rounded-full animate-spin"></div>
                    <span>Saving...</span>
                  </>
                ) : autoSaveStatus === 'saving' ? (
                  <>
                    <div className="w-3 h-3 border-2 border-neutral-300 border-t-blue-500 rounded-full animate-spin"></div>
                    <span>Auto-saving...</span>
                  </>
                ) : hasUnsavedChanges ? (
                  autoSaveStatus === 'pending' ? (
                    <>
                      <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></div>
                      <span>Save Now</span>
                    </>
                  ) : (
                    <>
                      <span className="text-primary-500">💾</span>
                      <span>Save Now</span>
                    </>
                  )
                ) : (
                  <>
                    <span className="text-success-500">✓</span>
                    <span>Saved</span>
                  </>
                )}
              </span>
            </button>
          )}

          {/* Save Status (Non-Editor Mode) */}
          {!isEditorMode && saveConfig && SaveIcon && (
            <div className="hidden sm:flex items-center gap-2 text-xs">
              <SaveIcon className={`w-4 h-4 ${saveConfig.className}`} />
              <span className={saveConfig.className}>{saveConfig.text}</span>
            </div>
          )}

          {/* Auto-Save Indicator removed - status now shown in Save button */}

          {/* Settings Button (Editor Mode Only - requires currentSong for modal) */}
          {isEditorMode && onOpenSettings && currentSong && (
            <button
              ref={settingsButtonRef}
              data-testid="settings-button"
              className="daw-button flex items-center gap-2 px-3 py-2"
              onClick={onOpenSettings}
              aria-label="Open song settings"
              aria-haspopup="dialog"
              aria-expanded={isSettingsOpen}
            >
              <Cog6ToothIcon className="w-4 h-4" />
              <span>Settings</span>
            </button>
          )}

          {/* User Menu - Shared dropdown for account settings and logout */}
          <UserDropdownMenu mode="full" />
        </div>
      </div>


      {/* Mobile Tab Navigation - Simplified */}
      {panelState.isMobile && (
        <div className="flex items-center bg-black/10 border-b border-white/10">
          <button
            onClick={() => panelState.switchToTab('settings')}
            className={`flex-1 py-3 text-center text-sm font-medium transition-all duration-200 border-b-2 ${
              panelState.activeTab === 'settings'
                ? 'border-blue-400 text-blue-400'
                : 'border-transparent text-neutral-400 hover:text-white'
            }`}
          >
            Settings
          </button>
          <button
            onClick={() => panelState.switchToTab('editor')}
            className={`flex-1 py-3 text-center text-sm font-medium transition-all duration-200 border-b-2 ${
              panelState.activeTab === 'editor'
                ? 'border-blue-400 text-blue-400'
                : 'border-transparent text-neutral-400 hover:text-white'
            }`}
          >
            Editor
          </button>
        </div>
      )}
    </header>

    {/* Enhanced Song Metadata Dropdown */}
    {/* CRITICAL: Dropdown is now OUTSIDE <header> to escape stacking context */}
    {/* This allows it to render above editor content which comes after header in DOM */}
    {/* Note: backdrop-blur removed to prevent stacking context issues
        Higher opacity (bg-black/95) maintains visual separation without z-index complexity */}
    {showSongMeta && currentSong && (
      <>
      {/* Backdrop for closing dropdown on click outside - starts below header (top-16) */}
      <div
        data-testid="dropdown-backdrop"
        className="fixed inset-x-0 top-16 bottom-0 z-40"
        onClick={() => setShowSongMeta(false)}
        aria-hidden="true"
      />
      <div data-testid="song-details-dropdown" className="absolute top-16 left-0 right-0 bg-black/95 border-b border-white/20 shadow-2xl md:left-auto md:right-auto md:w-96 md:ml-64 md:rounded-b-xl md:border md:border-white/20 md:shadow-2xl pointer-events-none z-50">
        {/* Padding div stays pointer-events-none to allow clicks to pass through to toolbar below */}
        <div className="p-4 sm:p-6 pointer-events-none">
          {isEditingMeta ? (
            /* Editing Mode */
            /* Wrapper stays pointer-events-none to let clicks pass through to toolbar */
            <div className="space-y-4 pointer-events-none">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <span>✏️</span>
                  <span>Edit Song Details</span>
                </h3>
                {/* Re-enable pointer events only on interactive button */}
                <button
                  onClick={handleCancelEdit}
                  className="p-1 rounded-md hover:bg-white/10 transition-all duration-200 border border-white/10 hover:border-white/20 pointer-events-auto"
                >
                  <XMarkIcon className="w-4 h-4 text-neutral-400 hover:text-white transition-colors duration-200" />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">Title</label>
                {/* Re-enable pointer events on form input */}
                <input
                  data-testid="metadata-title-input"
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-white/20 bg-black/20 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all duration-200 text-sm backdrop-blur-sm pointer-events-auto"
                  placeholder="Enter song title..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-300 mb-2">Artist</label>
                {/* Re-enable pointer events on form input */}
                <input
                  data-testid="metadata-artist-input"
                  type="text"
                  value={editArtist}
                  onChange={(e) => setEditArtist(e.target.value)}
                  className="w-full px-3 py-2 border border-white/20 bg-black/20 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all duration-200 text-sm backdrop-blur-sm pointer-events-auto"
                  placeholder="Enter artist name..."
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">Status</label>
                  {/* Re-enable pointer events on select */}
                  <select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value as Song['status'])}
                    className="w-full px-3 py-2 border border-white/20 bg-black/20 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all duration-200 text-sm backdrop-blur-sm pointer-events-auto"
                  >
                    <option value="draft">Draft</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">Tags</label>
                  {/* Re-enable pointer events on form input */}
                  <input
                    type="text"
                    value={editTags}
                    onChange={(e) => setEditTags(e.target.value)}
                    className="w-full px-3 py-2 border border-white/20 bg-black/20 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all duration-200 text-sm backdrop-blur-sm pointer-events-auto"
                    placeholder="tag1, tag2..."
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                {/* Re-enable pointer events on buttons */}
                <button
                  data-testid="save-metadata-button"
                  onClick={handleSaveMetadata}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl pointer-events-auto"
                >
                  Save Changes
                </button>
                <button
                  data-testid="cancel-metadata-edit"
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-neutral-300 bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/30 rounded-lg transition-all duration-200 font-medium backdrop-blur-sm pointer-events-auto"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            /* View Mode */
            /* Wrapper stays pointer-events-none to let clicks pass through */
            <div className="space-y-3 pointer-events-none">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <span>🎵</span>
                  <span>Song Details</span>
                </h3>
                {isEditorMode && onSongUpdate && (
                  <button
                    data-testid="edit-metadata-button"
                    onClick={handleEditMetadata}
                    className="text-sm text-blue-400 hover:text-blue-300 font-medium flex items-center gap-1 transition-colors duration-200 pointer-events-auto"
                  >
                    <PencilIcon className="w-3 h-3" />
                    Edit
                  </button>
                )}
              </div>

              <div>
                <label className="text-xs font-medium text-neutral-400 uppercase tracking-wide">Title</label>
                <div className="text-sm text-white font-medium">{currentSong.title || 'Untitled Song'}</div>
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-400 uppercase tracking-wide">Artist</label>
                <div className="text-sm text-neutral-200">{currentSong.artist || 'No artist set'}</div>
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-400 uppercase tracking-wide">Status</label>
                <div className="text-sm text-neutral-200 capitalize">{(currentSong.status || 'draft').replace('_', ' ')}</div>
              </div>
              {currentSong.tags && currentSong.tags.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-neutral-400 uppercase tracking-wide">Tags</label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {currentSong.tags.map((tag, index) => (
                      <span key={index} className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded-md text-xs font-medium border border-blue-400/30">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-white/20">
                <div>
                  <label className="text-xs font-medium text-neutral-400 uppercase tracking-wide">Created</label>
                  <div className="text-sm text-neutral-200">{new Date(currentSong.created_at).toLocaleDateString()}</div>
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-400 uppercase tracking-wide">Modified</label>
                  <div className="text-sm text-neutral-200">{new Date(currentSong.updated_at).toLocaleDateString()}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      </>
    )}
    </>
  )
}
